import { z } from 'zod';
import { apiClient } from '../client';

/**
 * "Connecter le dialleur au CRM Swoft": the typed wrapper over
 * /api/integrations/swoft-connect.
 *
 * Nothing here ever carries a credential. The backend writes what Swoft
 * provisions on the tenant link and never reads it back, so the only things
 * this module sees are: is it connected, to which organisation, and which
 * organisations the person may pick from.
 */

/** One person of the connected organisation, offered as a pick for "who
 *  receives this client's meetings". Never a typed identifier. */
const MemberSchema = z.object({
  personId: z.string(),
  displayName: z.string(),
  emailAddress: z.string().default(''),
});

export type SwoftOrganisationMember = z.infer<typeof MemberSchema>;

const StatusSchema = z.object({
  connected: z.boolean(),
  organisationId: z.string().default(''),
  organisationName: z.string().default(''),
  connectionId: z.string().default(''),
  /** False while no super admin has configured the Swoft entry point. The
   *  screen then shows the manual configuration instead of a dead button. */
  oneClickAvailable: z.boolean().default(false),
  /** Who holds this client's meetings. Empty means nobody was picked: the
   *  screen has to say so rather than let an admin assume someone will
   *  show up. */
  meetingOwnerPersonId: z.string().default(''),
  meetingOwnerName: z.string().default(''),
  /** What the picker offers, snapshotted when the organisation was chosen.
   *  Empty while the roster could not be read: the screen says that
   *  instead of showing a list nothing can be chosen from. */
  members: z.array(MemberSchema).default([]),
});

export type SwoftConnectStatus = z.infer<typeof StatusSchema>;

const HandoverSchema = z.object({
  handoverId: z.string(),
  customerId: z.string(),
  organisations: z.array(z.object({ id: z.string(), name: z.string() })).default([]),
  /** Filled when the person belongs to exactly one organisation. It stays on
   *  screen and preselected, never hidden: they have to see which
   *  organisation is about to receive their calls. */
  preselectedOrganisationId: z.string().default(''),
});

export type SwoftConnectHandover = z.infer<typeof HandoverSchema>;

export async function getSwoftConnectStatus(customerId: string): Promise<SwoftConnectStatus> {
  const raw = await apiClient.get<unknown>(`/integrations/swoft-connect/customers/${encodeURIComponent(customerId)}/status`);
  return StatusSchema.parse(raw);
}

/**
 * Where the browser goes when the button is pressed. It is a full page
 * navigation on purpose: the route answers a 302 towards the Swoft consent
 * screen, which no fetch can follow on the user's behalf.
 */
export function swoftConnectAuthorizeUrl(customerId: string): string {
  return `/api/integrations/swoft-connect/customers/${encodeURIComponent(customerId)}/authorize`;
}

export async function getSwoftConnectHandover(handoverId: string): Promise<SwoftConnectHandover> {
  const raw = await apiClient.get<unknown>(`/integrations/swoft-connect/handovers/${encodeURIComponent(handoverId)}`);
  return HandoverSchema.parse(raw);
}

/** Provision against the chosen organisation. Swoft answers 403 when the
 *  person is not a member of it; the caller shows that sentence. */
export async function chooseSwoftOrganisation(handoverId: string, organisationId: string): Promise<SwoftConnectStatus> {
  const raw = await apiClient.post<unknown>(
    `/integrations/swoft-connect/handovers/${encodeURIComponent(handoverId)}/choose`,
    { organisation_id: organisationId },
  );
  return StatusSchema.parse(raw);
}

/**
 * Who, at the client, receives the meetings. Picked from the roster
 * snapshotted at connection time, never typed: an empty personId clears the
 * choice, which is a valid answer and means nobody.
 */
export async function setSwoftMeetingOwner(customerId: string, personId: string): Promise<SwoftConnectStatus> {
  const raw = await apiClient.post<unknown>(
    `/integrations/swoft-connect/customers/${encodeURIComponent(customerId)}/meeting-owner`,
    { person_id: personId },
  );
  return StatusSchema.parse(raw);
}

export async function disconnectSwoftConnect(customerId: string): Promise<SwoftConnectStatus> {
  const raw = await apiClient.post<unknown>(
    `/integrations/swoft-connect/customers/${encodeURIComponent(customerId)}/disconnect`,
    {},
  );
  return StatusSchema.parse(raw);
}
